import React, { useState, useRef, useCallback, useEffect } from 'react';

function ZoomControl({ zoom, onZoomChange, onFitZoom }) {
  const zoomPercent = Math.round(zoom * 100);
  const dragRef = useRef(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ mx: 0, my: 0, px: 0, py: 0 });
  const [initialized, setInitialized] = useState(false);

  // Position bottom-center on first render
  useEffect(() => {
    const el = dragRef.current;
    if (!el || initialized) return;
    const rect = el.getBoundingClientRect();
    setPos({
      x: (window.innerWidth - rect.width) / 2,
      y: window.innerHeight - rect.height - 12,
    });
    setInitialized(true);
  }, [initialized]);

  const onMouseDown = useCallback((e) => {
    // Don't start drag if clicking on interactive elements (buttons, inputs, or their children)
    if (e.target.closest('button') || e.target.closest('input')) return;
    e.preventDefault();
    setIsDragging(true);
    dragStartRef.current = { mx: e.clientX, my: e.clientY, px: pos.x, py: pos.y };
  }, [pos]);

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e) => {
      const { mx, my, px, py } = dragStartRef.current;
      setPos({ x: px + e.clientX - mx, y: py + e.clientY - my });
    };
    const onUp = () => setIsDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isDragging]);

  return (
    <div
      ref={dragRef}
      className="fixed z-50 flex items-center gap-1.5 bg-white/95 backdrop-blur-sm rounded-full shadow-lg border border-gray-200/80 px-3 py-1.5 select-none"
      style={{
        left: pos.x,
        top: pos.y,
        cursor: isDragging ? 'grabbing' : 'grab',
        transition: isDragging ? 'none' : 'box-shadow 0.2s',
        boxShadow: isDragging
          ? '0 8px 30px rgba(0,0,0,0.15)'
          : '0 2px 12px rgba(0,0,0,0.08)',
      }}
      onMouseDown={onMouseDown}
    >
      {/* Drag handle */}
      <svg className="w-3.5 h-3.5 text-gray-300 mr-0.5 flex-shrink-0" viewBox="0 0 16 16" fill="currentColor">
        <circle cx="4" cy="4" r="1.5"/><circle cx="4" cy="8" r="1.5"/><circle cx="4" cy="12" r="1.5"/>
        <circle cx="10" cy="4" r="1.5"/><circle cx="10" cy="8" r="1.5"/><circle cx="10" cy="12" r="1.5"/>
      </svg>

      <button
        onClick={() => onZoomChange(Math.max(0.05, zoom - 0.05))}
        className="w-6 h-6 flex items-center justify-center text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-full text-sm font-medium transition-colors"
        data-tooltip="Verkleinern"
        data-tooltip-pos="bottom"
      >
        <svg className="w-3.5 h-3.5 pointer-events-none" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 8h10"/></svg>
      </button>

      <input
        type="range"
        min="5"
        max="300"
        value={zoomPercent}
        onChange={(e) => onZoomChange(Number(e.target.value) / 100)}
        className="w-24 h-1 bg-gray-200 rounded-full appearance-none cursor-pointer accent-blue-600"
        style={{ margin: '0 2px' }}
      />

      <button
        onClick={() => onZoomChange(Math.min(3, zoom + 0.05))}
        className="w-6 h-6 flex items-center justify-center text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-full text-sm font-medium transition-colors"
        data-tooltip="Vergrößern"
        data-tooltip-pos="bottom"
      >
        <svg className="w-3.5 h-3.5 pointer-events-none" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 8h10M8 3v10"/></svg>
      </button>

      <span className="text-[11px] text-gray-500 w-10 text-center font-mono tabular-nums">{zoomPercent}%</span>

      <div className="w-px h-4 bg-gray-200 mx-0.5" />

      {onFitZoom && (
        <button
          onClick={onFitZoom}
          className="text-[11px] text-blue-600 hover:text-blue-800 px-2 py-0.5 rounded-full hover:bg-blue-50 font-medium transition-colors"
          data-tooltip="Auf Bildschirm einpassen"
          data-tooltip-pos="bottom"
        >
          Einpassen
        </button>
      )}
    </div>
  );
}

export default ZoomControl;
